import { z } from 'zod';
import type { TracePhase } from './trace-tool-filters';
import {
  extractReadFilePath,
  getReadFileDisplayName,
  isSkillFilePath,
} from './read-file-tool';

const AuthenticationDetailsArgsSchema = z.object({
  needRefresh: z.boolean().optional(),
});

const isAuthenticationRefreshRequested = (toolArgs: unknown): boolean => {
  const parsed = AuthenticationDetailsArgsSchema.safeParse(toolArgs);

  return parsed.success && !!parsed.data.needRefresh;
};

export const normalizeToolName = (toolName: string): string => {
  return toolName.replace(/_/g, '-').toLowerCase();
};

export function isMcpToolName(toolName: string): boolean {
  const normalized = toolName.toLowerCase();

  return normalized.startsWith('mcp__') || normalized.startsWith('mcp_');
}

export function getMcpToolDisplayName(toolName: string): string {
  if (toolName.startsWith('mcp__')) {
    const withoutPrefix = toolName.replace(/^mcp__/, '');
    const parts = withoutPrefix.split('__');
    const serverName = parts[0] ?? '';
    const humanServer =
      serverName.charAt(0).toUpperCase() + serverName.slice(1);

    return humanServer ? `MCP: ${humanServer}` : 'MCP Tool';
  }

  const withoutPrefix = toolName.replace(/^mcp_/, '');
  const segments = withoutPrefix.split('_').filter(Boolean);
  const terraIndex = segments.indexOf('terra');
  const labelSegments =
    terraIndex >= 0 ? segments.slice(terraIndex + 1) : segments.slice(1);

  if (labelSegments[0] === 'terra') {
    labelSegments.shift();
  }

  const label = labelSegments
    .join(' ')
    .replace(/\bbloodhound[_\s]+cypher\b/gi, 'AD graph query')
    .replace(/\bbloodhound\b/gi, 'AD graph')
    .replace(/\bbh\b/gi, 'AD graph')
    .replace(/\bcypher\b/gi, 'graph query')
    .replace(/\bmythic\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) =>
      word.toLowerCase() === 'ad'
        ? 'AD'
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');

  return label ? `MCP: ${label}` : 'MCP Tool';
}

const HTTP_EXCHANGE_TOOL_NAMES = new Set([
  'http-tool',
  'dynamic-http-tool',
  'validated-http-tool',
  'http-executor',
  'ai-discovery-http',
  'ai-discovery-fetch-openapi',
  'sse-client',
  'ai-discovery-sse',
  'websocket-client',
  'ai-discovery-websocket',
  'ai-discovery-mcp',
]);

export function isHttpExchangeTool(toolName: string): boolean {
  return HTTP_EXCHANGE_TOOL_NAMES.has(normalizeToolName(toolName));
}

export function isNeoWebSocketStreamingTool(toolName: string): boolean {
  return normalizeToolName(toolName) === 'websocket-client';
}

const BROWSER_TOOL_DISPLAY_NAMES: Record<string, string> = {
  'browser-open': 'Open browser',
  'browser-navigate': 'Navigate',
  'browser-snapshot': 'Snapshot',
  'browser-click': 'Click',
  'browser-type': 'Type',
  'browser-press-key': 'Press key',
  'browser-handle-dialog': 'Dialog',
  'browser-close': 'Close browser',
  'browser-scroll': 'Scroll',
  'browser-take-screenshot': 'Screenshot',
  'browser-console-messages': 'Console',
  'browser-network-requests': 'Network',
  'browser-network-request': 'Network request',
  'browser-tabs': 'Tabs',
  'browser-select-option': 'Select option',
  'browser-hover': 'Hover',
  'browser-fill-form': 'Fill form',
  'browser-wait-for': 'Wait for',
  'browser-find': 'Find',
  'browser-file-upload': 'Upload files',
  'browser-get-content': 'Page source',
  'browser-evaluate': 'Evaluate JS',
  'browser-history': 'History',
};

const BROWSER_SESSION_TOOL_NAMES: ReadonlySet<string> = new Set(
  Object.keys(BROWSER_TOOL_DISPLAY_NAMES),
);

export function isBrowserToolName(toolName: string): boolean {
  return BROWSER_SESSION_TOOL_NAMES.has(normalizeToolName(toolName));
}

export function getBrowserToolDisplayName(
  toolName: string,
): string | undefined {
  return BROWSER_TOOL_DISPLAY_NAMES[normalizeToolName(toolName)];
}

export type TraceToolRenderConfig = {
  hideArgs: boolean;
  hideRawError: boolean;
  constrainOutputHeight: boolean;
  preferHarOverToolOutput: boolean;
};

type TraceToolRenderConfigBase = {
  hideArgs: boolean;
  hideRawError: boolean;
  constrainOutputHeight: boolean;
};

export type TraceToolRenderContext = {
  preferHarOverToolOutput?: boolean;
};

const DEFAULT_RENDER_CONFIG_BASE: TraceToolRenderConfigBase = {
  hideArgs: false,
  hideRawError: false,
  constrainOutputHeight: true,
};

const HTTP_EXCHANGE_RENDER_CONFIG_BASE: TraceToolRenderConfigBase = {
  hideArgs: true,
  hideRawError: true,
  constrainOutputHeight: false,
};

const NAVIGATE_WEB_RENDER_CONFIG_BASE: TraceToolRenderConfigBase = {
  hideArgs: false,
  hideRawError: false,
  constrainOutputHeight: false,
};

const resolveBaseConfig = (toolName: string): TraceToolRenderConfigBase => {
  if (isHttpExchangeTool(toolName)) {
    return HTTP_EXCHANGE_RENDER_CONFIG_BASE;
  }

  if (normalizeToolName(toolName) === 'navigate-web') {
    return NAVIGATE_WEB_RENDER_CONFIG_BASE;
  }

  return DEFAULT_RENDER_CONFIG_BASE;
};

export function getTraceToolRenderConfig(
  toolName: string,
  context: TraceToolRenderContext = {},
): TraceToolRenderConfig {
  const base = resolveBaseConfig(toolName);

  return {
    hideArgs: base.hideArgs,
    hideRawError: base.hideRawError,
    constrainOutputHeight: base.constrainOutputHeight,
    preferHarOverToolOutput: context.preferHarOverToolOutput ?? false,
  };
}

export const getToolDisplayName = (
  toolName?: string,
  toolArgs?: unknown,
  tracePhase: TracePhase = 'attack',
): string => {
  const normalizedName = toolName ? normalizeToolName(toolName) : '';

  const browserDisplayName = BROWSER_TOOL_DISPLAY_NAMES[normalizedName];

  if (browserDisplayName) {
    return browserDisplayName;
  }

  switch (normalizedName) {
    case 'load-tools':
      return 'Load tools';
    case 'read-file': {
      const filePath = extractReadFilePath(toolArgs);

      if (tracePhase === 'co-pilot' && isSkillFilePath(filePath)) {
        return 'Read skill';
      }

      if (tracePhase === 'post-attack') {
        return `Read ${getReadFileDisplayName(filePath)}`;
      }

      if (tracePhase === 'co-pilot') {
        return getReadFileDisplayName(filePath);
      }

      return /\/skill\.md$/i.test(filePath ?? '') ? 'Read skill' : 'Read file';
    }

    case 'grep':
      return 'Search files';
    case 'read-emails':
      return 'Read Emails';
    case 'tag-emails':
      return 'Tag Emails';
    case 'save-relevant-emails':
      return 'Save Emails';
    case 'coder':
    case 'coder-tool':
    case 'sub-agent-coder':
      return 'Run Code';
    case 'file-upload-client':
      return 'File Upload Tool';
    case 'prepare-file':
      return 'Prepare File';
    case 'http-tool':
    case 'dynamic-http-tool':
    case 'ai-discovery-http':
    case 'ai-discovery-fetch-openapi':
      return 'HTTP Request';
    case 'ai-discovery-mcp':
      return 'MCP Request';
    case 'python':
    case 'python-tool':
      return 'Run Code';
    case 'todo-write':
      return 'Todos';
    case 'sub-agent-intelligence':
      return 'Thinking';
    case 'navigate-web':
      return 'Browse Web';
    case 'enterprise-search':
      return 'Enterprise Search';
    case 'out-of-band-detection':
      return 'OOB Detection';
    case 'search-endpoints':
      return 'Search Endpoints';
    case 'sse-client':
    case 'ai-discovery-sse':
      return 'SSE Client';
    case 'get-endpoint-details':
    case 'endpoint-details':
    case 'endpoint-details-tool':
    case 'get-tracked-endpoint-schema':
    case 'endpoint-read':
      return 'Read Endpoint';
    case 'websocket-client':
    case 'ai-discovery-websocket':
      return 'WebSocket Client';
    case 'get-authentication-details':
    case 'authentication-details':
    case 'authentication-details-tool':
      return isAuthenticationRefreshRequested(toolArgs)
        ? 'Authenticate'
        : 'Read authentication details';
    case 'get-mcp-authentication-details':
    case 'mcp-authentication-details':
      return 'MCP Auth Details';
    case 'mcp-connection-status-tool':
      return 'MCP Connection Status';
    case 'mcp-test-connection-tool':
      return 'MCP Test Connection';
    case 'authenticate':
      return 'Authenticate';
    case 'login-tool':
      return 'Login';
    case 'end-test':
    case 'ai-discovery-complete':
    case 'ai-discovery-verdict':
      return 'Summary';
    case 'business-context-tool':
    case 'get-business-context':
      return 'Ask Business Context';
    case 'get-neo-memories':
      return 'Recalling Memory';
    case 'ask-user':
      return 'Answer';
    case 'render-visualization':
      return 'Chart';
    case 'remote-c2-command': {
      const argsRecord =
        toolArgs && typeof toolArgs === 'object'
          ? (toolArgs as Record<string, unknown>)
          : undefined;
      const command =
        typeof argsRecord?.command === 'string'
          ? argsRecord.command
          : undefined;

      return command ? `Remote Command: ${command}` : 'Remote Command';
    }

    case 'password-audit-crack':
      return 'Password Audit';
    case 'list-ad-entities':
      return 'List AD Entities';
    case 'get-ad-entity-details':
      return 'AD Entity';
    case 'get-ad-entity-relationships':
      return 'AD Relationships';
    case 'query-ad-graph':
      return 'AD Graph';
    case 'create-internal-network-discovery-signal':
      return 'Create Signal';
    case 'get-existing-internal-network-discovery-signals':
      return 'List Signals';
    case 'create-internal-network-scenario-draft':
      return 'Scenario Draft';
    case 'mythic-send-task': {
      const argsRecord =
        toolArgs && typeof toolArgs === 'object'
          ? (toolArgs as Record<string, unknown>)
          : undefined;
      const command =
        typeof argsRecord?.command === 'string'
          ? argsRecord.command
          : undefined;

      return command ? `Send Task: ${command}` : 'Send Task';
    }

    case 'mythic-get-task-output':
      return 'Task Output';
    case 'mythic-list-commands':
      return 'List Commands';
    case 'mythic-list-callbacks':
      return 'List Callbacks';
    case 'mythic-get-callback-info':
      return 'Callback Info';
    default:
      if (toolName && isMcpToolName(toolName)) {
        return getMcpToolDisplayName(toolName);
      }

      return toolName
        ? toolName
            .replace(/[-_]/g, ' ')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : 'Tool';
  }
};
